import './PersonalInformation.scss';

import * as Yup from 'yup';

import { Button, Form } from 'react-bootstrap';
import {
  checkUserStatus,
  getCandidatePersonalInfo,
  getUser,
  putCandidatePersonalInfo,
  selectPersonalInfo,
  selectUserStatus
} from '../../ducks/entities';
import {
  scrollToBannerBottom,
  scrollToTop,
  setTitle
} from '../../utils/generic';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AboutYou from '../../components/AboutYou/AboutYou';
import Coordinates from '../../components/Coordinates/Coordinates';
import { Formik } from 'formik';
import Language from '../../components/Language/Language';
import LoaderComponent from '../../components/Loader/LoaderComponents';
import { PROFIL_TABS } from '../../utils/constants';
import { PersonalInfo } from '../../models/candidate';
import ProfileSteps from '../../components/ProfileSteps/ProfileSteps';
import ScrollToFieldError from '../../components/ScrollToFieldError/ScrollToFieldError';
import UbiAccountInfo from '../../components/UbiAccountInfo/UbiAccountInfo';
import imageStep from '../../assets/images/ubisoft-playtest-profile-mobile-steps-1-4.webp';
import profilMessage from './messages';
import { selectAuth } from '../../ducks/auth';
import siteMessages from '../root/siteMessages';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
interface Props {
  setIsFieldModified(value: boolean): void;
}

const SECTION_NAME = 'personalInfo';

const PersonalInformation = (props: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const personalInfo = useSelector(selectPersonalInfo);
  const [next, setNext] = useState(true);
  const navigate = useNavigate();
  const [schema, setSchema] = useState<any>();
  const [coordinatesSchema, setCoordinatesSchema] = useState<any>();
  const [aboutYouSchema, setAboutYouSchema] = useState<any>();
  const [languageSchema, setLanguageSchema] = useState<any>();
  const [ubiAccountSchema, setUbiAccountSchema] = useState<any>();
  const userStatus = useSelector(selectUserStatus);
  const location = useLocation();
  const [validateOnChange, setValidateOnChange] = useState(false);

  useEffect(() => {
    setTitle(intl.formatMessage({ ...profilMessage.personalinformation }));
  }, []);

  useEffect(() => {
    dispatch(getUser(auth.userId));
    dispatch(getCandidatePersonalInfo());
  }, [auth.userId]);

  useEffect(() => {
    location.state?.prevRoute ? scrollToBannerBottom() : scrollToTop();
  }, [location.state]);

  useEffect(() => {
    setSchema({
      ...coordinatesSchema,
      ...aboutYouSchema,
      ...languageSchema,
      ...ubiAccountSchema
    });
  }, [coordinatesSchema, aboutYouSchema, languageSchema]);

  const onSubmit = async (values: PersonalInfo, { setSubmitting }: any) => {
    setSubmitting(true);
    props.setIsFieldModified(false);
    await (dispatch(putCandidatePersonalInfo(values)) as any)
      .then(() => {
        toast.success(
          intl.formatMessage({ ...siteMessages.updateProfileSuccess })
        );
        setSubmitting(false);
        dispatch(checkUserStatus());
        if (next) {
          const index = Object.keys(userStatus).findIndex(
            (k) => !(userStatus as any)[k] && k !== SECTION_NAME
          );
          navigate(
            location.pathname.replace(
              'personalinformation',
              PROFIL_TABS[index].pathname
            ),
            { state: { prevRoute: location.pathname }, replace: true }
          );
        }
      })
      .catch(() => {
        props.setIsFieldModified(true);
        toast.error(intl.formatMessage({ ...siteMessages.updateProfileFail }));
      });
  };

  const onSetField = (
    field: string,
    value: any,
    setFieldValue: any,
    shouldValidate?: boolean | undefined
  ) => {
    props.setIsFieldModified(true);
    setFieldValue(field, value, shouldValidate);
  };

  return (
    <div className="personal-information">
      <div className="required-field">
        <p>{intl.formatMessage({ ...profilMessage.fieldRequired })}</p>
      </div>
      {personalInfo ? (
        <Formik
          initialValues={personalInfo}
          validationSchema={Yup.object().shape(schema)}
          onSubmit={onSubmit}
          validateOnChange={validateOnChange}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
            isSubmitting,
            setFieldValue
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              <UbiAccountInfo
                updateSchema={(schema) => setUbiAccountSchema(schema)}
              />
              <Coordinates
                values={values}
                errors={errors}
                handleChange={(e: any) => {
                  handleChange(e);
                  props.setIsFieldModified(true);
                }}
                updateSchema={(schema) => setCoordinatesSchema(schema)}
              />
              <AboutYou
                values={values}
                errors={errors}
                setFieldValue={(
                  field: string,
                  value: any,
                  shouldValidate?: boolean | undefined
                ) => onSetField(field, value, setFieldValue, shouldValidate)}
                updateSchema={(schema) => setAboutYouSchema(schema)}
              />
              <Language
                values={values}
                errors={errors}
                setFieldValue={(
                  field: string,
                  value: any,
                  shouldValidate?: boolean | undefined
                ) => onSetField(field, value, setFieldValue, shouldValidate)}
                updateSchema={(schema) => setLanguageSchema(schema)}
              />
              <div className="btns-save">
                <Button
                  variant="primary"
                  disabled={isSubmitting}
                  onClick={() => {
                    !Object.keys(userStatus).find(
                      (k) => !(userStatus as any)[k] && k !== SECTION_NAME
                    )
                      ? setNext(false)
                      : setNext(true);
                    setValidateOnChange(true);
                    handleSubmit();
                  }}
                >
                  <LoaderComponent
                    isLoading={isSubmitting}
                    className="lds-small"
                  >
                    {!Object.keys(userStatus).find(
                      (k) => !(userStatus as any)[k] && k !== SECTION_NAME
                    )
                      ? intl.formatMessage({ ...siteMessages.save })
                      : intl.formatMessage({ ...siteMessages.saveAndNext })}
                  </LoaderComponent>
                </Button>
              </div>
              <ProfileSteps step={1} steps={4} image={imageStep} />
            </Form>
          )}
        </Formik>
      ) : (
        <div className="loading">
          {[...Array(11).keys()].map((i) => (
            <p key={`personalInfo-${i}`} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PersonalInformation;
